import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatTooltipModule } from '@angular/material/tooltip';

import { HrClock } from '@app/shared/_models/hr';
import { HR_CLOCK_IN, HR_CLOCK_SOURCE_KIOSK, HR_CLOCK_SOURCE_MANUAL, HR_CLOCK_SOURCE_SESSION } from '@app/shared/constants';
import { isoAtWall, parseTz, pickedDateStr } from '@app/shared/time';
import { minutesOfHhmm } from '../../hr-schedule-engine';

// Correction des pointages d'un employé (gestionnaire). Les pointages bruts sont IMMUABLES :
//   - « annuler » un pointage = event 2802 (cancelled = true, tracé) ;
//   - « ajouter » un pointage oublié = INSERT source 3 (manuel), horodaté par le gestionnaire.
// Le dialog rend UNE action à la fois ; la page poste puis recharge.
export interface ClockEditDialogData {
	employeeName: string;
	id_employee: number;
	clocks: HrClock[];		// pointages de la période affichée (annulés compris, barrés)
}

// Le champ « Commentaire (motif de la correction / annulation) » accompagne les DEUX gestes :
// l'annulation comme l'ajout transportent le motif (HrClock.comment), sinon la moitié de la
// promesse du libellé se perdait en route.
export type ClockEditDialogResult =
	| { kind: 'cancel'; id: number; comment: string | null }
	| { kind: 'add'; ts: string; io: number; comment: string | null }
	| undefined;

@Component({
	selector: 'app-hr-clock-edit-dialog',
	standalone: true,
	imports: [ReactiveFormsModule, MatDialogModule, MatFormFieldModule, MatInputModule, MatSelectModule,
		MatButtonModule, MatIconModule, MatDividerModule, MatDatepickerModule, MatTooltipModule],
	templateUrl: './clock-edit-dialog.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: './clock-edit-dialog.component.scss',
})
export class ClockEditDialogComponent {

	public data: ClockEditDialogData = inject(MAT_DIALOG_DATA);
	private dialogRef = inject(MatDialogRef<ClockEditDialogComponent>);

	public form = new FormGroup({
		dat: new FormControl<string | Date | null>(null),
		time: new FormControl<string>(''),
		io: new FormControl<number>(HR_CLOCK_IN),
		comment: new FormControl<string>(''),
	});
	public errorMessage = '';

	get sortedClocks(): HrClock[] {
		return this.data.clocks.slice().sort((a, b) => (a.ts < b.ts ? -1 : 1));
	}

	clockLabel(c: HrClock): string {
		const d = parseTz(c.ts);
		const when = d ? d.format('DD/MM HH:mm') : c.ts;
		const io = c.io === HR_CLOCK_IN ? 'Entrée' : 'Sortie';
		const source = c.source === HR_CLOCK_SOURCE_KIOSK ? 'kiosque' : c.source === HR_CLOCK_SOURCE_SESSION ? 'session' : c.source === HR_CLOCK_SOURCE_MANUAL ? 'manuel' : '?';
		return when + ' — ' + io + ' (' + source + ')' + (c.comment ? ' — ' + c.comment : '');
	}

	onCancelClock(c: HrClock): void {
		this.dialogRef.close({ kind: 'cancel', id: c.id, comment: this.form.get('comment')!.value?.trim() || null } as ClockEditDialogResult);
	}

	onAdd(): void {
		const dat = pickedDateStr(this.form.get('dat')!.value);
		const min = minutesOfHhmm(this.form.get('time')!.value);
		if (!dat || min === null) { this.errorMessage = 'Choisissez la date et l\'heure du pointage.'; return; }
		const ts = isoAtWall(dat, min);
		if (!ts) { this.errorMessage = 'Date/heure invalides.'; return; }
		this.dialogRef.close({
			kind: 'add', ts, io: this.form.get('io')!.value ?? HR_CLOCK_IN,
			comment: this.form.get('comment')!.value?.trim() || null,
		} as ClockEditDialogResult);
	}

	onClose(): void {
		this.dialogRef.close(undefined);
	}
}
